import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { type ComponentPropsWithRef, type ReactNode, useState } from "react";
import { RAID_ROLE_LABELS, type RaidRole } from "@/shared/lib/roles";
import { cn } from "@/shared/lib/utils";
import { groupPlayers, type RoleGroupKey } from "../setup.grouping";
import type { PlayerChange } from "../setup.overrides";
import type { SetupPlayer } from "../setup.types";
import { PlayerContextMenu } from "./PlayerContextMenu";
import { SetupPlayerRow } from "./SetupPlayerRow";

type RoleGroup = { key: RoleGroupKey; players: SetupPlayer[] };

const ROLE_GROUP_LABELS: Record<RoleGroupKey, string> = {
  ...RAID_ROLE_LABELS,
  none: "Utan roll",
};

type BoardHandlers = {
  onChange: (player: SetupPlayer, change: PlayerChange) => void;
  onDeleteStandIn: (id: string) => void;
  onEditStandIn: (player: SetupPlayer) => void;
};

// Pure drop-decision table. `overId` is a role droppable id: "role:<key>".
// Returns null for no-ops (same role, or the unassigned "role:none" block).
// Benching is not a drop target — it lives in the right-click menu / "Ej med" panel.
function decideDrop(player: SetupPlayer, overId: string): PlayerChange | null {
  const current = `role:${player.raidRole ?? "none"}`;
  if (overId === current) return null;

  const role = overId.slice("role:".length);
  // The unassigned block is not a target for role changes.
  if (role === "none") return null;

  return { raidRole: role as RaidRole, benched: false };
}

export function SetupBoard({
  players,
  editable,
  onChange,
  onDeleteStandIn,
  onEditStandIn,
}: {
  players: SetupPlayer[];
  editable: boolean;
} & BoardHandlers) {
  const grouped = groupPlayers(players);
  const [activePlayer, setActivePlayer] = useState<SetupPlayer | null>(null);
  const sensors = useSensors(
    // 4px activation distance keeps plain clicks / right-clicks from starting a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  function handleDragStart({ active }: DragStartEvent) {
    setActivePlayer((active.data.current?.player as SetupPlayer) ?? null);
  }

  function handleDragEnd({ active, over }: DragEndEvent) {
    setActivePlayer(null);
    if (!over) return;
    const player = active.data.current?.player as SetupPlayer | undefined;
    if (!player) return;
    const change = decideDrop(player, String(over.id));
    if (change) onChange(player, change);
  }

  // Bare row in read-only mode; wrapped in the right-click menu + draggable when editable.
  function renderRow(p: SetupPlayer, className?: string) {
    const key = `${p.kind}:${p.id}`;
    if (!editable) {
      return <SetupPlayerRow key={key} player={p} className={className} />;
    }
    return (
      <PlayerContextMenu
        key={key}
        player={p}
        onChange={(change) => onChange(p, change)}
        onDeleteStandIn={onDeleteStandIn}
        onEditStandIn={onEditStandIn}
      >
        <DraggableRow player={p}>
          <SetupPlayerRow player={p} className={className} />
        </DraggableRow>
      </PlayerContextMenu>
    );
  }

  function roleBlockBody(group: RoleGroup) {
    return (
      <>
        <div className="flex items-baseline gap-2 px-2 pt-2 text-xs font-medium text-muted-foreground">
          {ROLE_GROUP_LABELS[group.key]}
          <span>{group.players.length}</span>
        </div>
        <div className="flex flex-col gap-1.5 p-1.5">
          {group.players.map((p) => renderRow(p))}
        </div>
      </>
    );
  }

  // A role's bordered column: min 13rem, grows to fill the row; the rightmost one
  // wraps to the next row when it no longer fits.
  function roleColumnBox(group: RoleGroup) {
    return (
      <div
        key={group.key}
        data-col
        className="w-full shrink-0 min-[485px]:w-52 rounded-md border border-border"
      >
        <DropZone editable={editable} id={`role:${group.key}`}>
          {roleBlockBody(group)}
        </DropZone>
      </div>
    );
  }

  // select-none covers everything inside — headings, role-block headers, and cards.
  // One flex-wrap of role columns; the setup's rightmost column wraps to the next
  // row when the window narrows (the rules rail wraps first, in SetupPage). Bench
  // and absent players live in the shared "Ej med" panel below (see SetupPage).
  const board = (
    <div className="select-none" data-setup-board>
      <section className="space-y-1">
        <h2 className="text-sm font-medium text-muted-foreground">
          Setup ({players.length - grouped.bench.length})
        </h2>
        <div className="flex flex-wrap items-start gap-1.5">
          {/* Grouping emits tank/healer/melee_dps/ranged_dps (always) then "none"
              (only when non-empty), already in render order — see groupPlayers. */}
          {grouped.roleGroups.map((g) => roleColumnBox(g))}
        </div>
      </section>
    </div>
  );

  if (!editable) {
    return board;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActivePlayer(null)}
    >
      {board}
      {/* No drop animation — the optimistic move relocates the card instantly,
          and the default animation would first slide the overlay back to its origin. */}
      <DragOverlay dropAnimation={null} className="cursor-default">
        {activePlayer && <SetupPlayerRow player={activePlayer} />}
      </DragOverlay>
    </DndContext>
  );
}

// Droppable when editable, plain styled div otherwise (read-only mode has no DndContext).
function DropZone({
  editable,
  id,
  className,
  children,
}: {
  editable: boolean;
  id: string;
  className?: string;
  children: ReactNode;
}) {
  return editable ? (
    <DroppableBlock id={id} className={className}>
      {children}
    </DroppableBlock>
  ) : (
    <div className={className}>{children}</div>
  );
}

// Highlights itself while a draggable hovers over it.
function DroppableBlock({
  id,
  className,
  children,
}: {
  id: string;
  className?: string;
  children: ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef} className={cn(className, isOver && "bg-muted/50")}>
      {children}
    </div>
  );
}

// Direct child of ContextMenuTrigger's Slot: spreads the trigger's merged props
// (onContextMenu, etc.) and composes its ref with dnd-kit's setNodeRef, so
// right-click menu and drag both work on the same node. Only mounted when editable.
function DraggableRow({
  player,
  ref,
  children,
  onPointerDown,
  ...rest
}: ComponentPropsWithRef<"div"> & { player: SetupPlayer }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `${player.kind}:${player.id}`,
    data: { player },
  });
  return (
    <div
      ref={(node) => {
        setNodeRef(node);
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      }}
      className={cn("cursor-default", isDragging && "opacity-50")}
      {...rest}
      {...listeners}
      {...attributes}
      // Both handlers must fire: Radix's (from the trigger) arms the touch
      // long-press timer for the context menu — it ignores mouse, and movement
      // cancels it — while dnd-kit's arms the drag (4px activation distance).
      onPointerDown={(e) => {
        onPointerDown?.(e);
        listeners?.onPointerDown?.(e);
      }}
    >
      {children}
    </div>
  );
}
